import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { flatten } from 'flat'

import { ALLOWED_MAC_ADDRESS_CHARACTERS, MAC_ADDRESS_WITH_MASK } from '../constants/mac-address'
import { dateFormats } from '@core/constants/dateFormats'
import { AuthService } from '@app/modules/auth/auth.service'
import { uniq, uniqBy } from 'lodash-es'

import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

@Injectable({ providedIn: 'root' })
export class UtilityService {
  private textData: string

  constructor(
    private translateService: TranslateService,
    private readonly authService: AuthService,
  ) {}

  dayjsDuration(value: string | number): string {
    const durationValue = dayjs.duration(Number(value), 'seconds')
    const years = durationValue.years()
    const months = durationValue.months()
    const days = durationValue.days()
    const hours = durationValue.hours()
    const minutes = durationValue.minutes()
    const seconds = durationValue.seconds()

    const yearsDisplay = years > 0 ? (this.translateService.instant('ABBREVIATIONS.Y', { value: years }) as string) : null
    const monthsDisplay = months > 0 ? (this.translateService.instant('ABBREVIATIONS.MO', { value: months }) as string) : null
    const daysDisplay = days > 0 ? (this.translateService.instant('ABBREVIATIONS.DAYS', { value: days }) as string) : null
    const hoursDisplay = hours > 0 ? (this.translateService.instant('ABBREVIATIONS.H', { value: hours }) as string) : null
    const minutesDisplay =
      hours > 0 || (hours === 0 && minutes > 0) ? (this.translateService.instant('ABBREVIATIONS.M', { value: minutes }) as string) : null
    const secondsDisplay = seconds >= 0 ? (this.translateService.instant('ABBREVIATIONS.S', { value: seconds }) as string) : null

    const mergedTime = [yearsDisplay, monthsDisplay, daysDisplay, hoursDisplay, minutesDisplay, secondsDisplay]
      .filter(Boolean)
      .slice(0, 2)
      .join(' ')

    return mergedTime
  }

  copyToClipboard(text: string): void {
    this.textData = text
    document.addEventListener('copy', this.handleCopy)
    document.execCommand('copy')
  }

  toMacAddress(value: string): string {
    if (!value) return

    let result = ''
    let idxAtMask = -1

    for (let i = 0; i < value.length; i++) {
      const currentChar: string = value.charAt(i)

      if (ALLOWED_MAC_ADDRESS_CHARACTERS.has(currentChar)) {
        if (idxAtMask === 1) {
          result += ':'
          idxAtMask = -1
        }

        result += currentChar
        idxAtMask++
      }
    }

    return result.length <= MAC_ADDRESS_WITH_MASK ? result : result.substr(0, MAC_ADDRESS_WITH_MASK)
  }

  macAddressToString(macAddress = ''): string {
    return macAddress?.split(':').join('') ?? ''
  }
  dayjsDateTimeFormatting(date?: string, format?: string): string {
    if (!date) return ''

    const dateFormat = format || `${this.authService.getCurrentUserDateFormat()} HH:mm` || dateFormats.dateTime

    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(date)

    const storedTimezone = localStorage.getItem('storedTimezone')
    const parsedTimezone = storedTimezone ? JSON.parse(storedTimezone) : null
    const userTimezone = parsedTimezone?.name || dayjs.tz.guess()

    const dayjsDate = isDateOnly ? dayjs.tz(date, 'YYYY-MM-DD', userTimezone) : dayjs.utc(date).tz(userTimezone)

    return dayjsDate.isValid() ? dayjsDate.format(dateFormat) : ''
  }

  distanceFormatting(distance: number | null): string {
    if (distance === null) return '-'

    return this.translateService.instant('ABBREVIATIONS.KM', { value: distance }) as string
  }

  private handleCopy = (event: ClipboardEvent): void => {
    event.preventDefault()
    event.clipboardData.setData('text/plain', this.textData)
    document.removeEventListener('copy', this.handleCopy)
  }

  getUserTimezone(): string {
    const storedTimezone = localStorage.getItem('storedTimezone')
    const parsedTimezone = storedTimezone ? JSON.parse(storedTimezone) : null
    return parsedTimezone?.name || dayjs.tz.guess()
  }

  formatToUserTimezone(date: string | number | Date | Dayjs): string {
    if (!date) return ''
    const userTz = this.getUserTimezone()
    const parsed = dayjs(date)
    return parsed.isValid() ? parsed.tz(userTz).format('YYYY-MM-DD HH:mm:ss') : String(date)
  }

  parseToUserTimezone(date: string | number | Date | Dayjs): Dayjs {
    const userTz = this.getUserTimezone()
    return dayjs(date).tz(userTz)
  }
}
